import { useCallback, useEffect, useRef, useState } from 'react';

import { usePrevious } from 'hooks/usePrevious';
import { useToast } from 'hooks/useToast';

export type UseMediaRecorderOptions = {
  isEnabled?: boolean;
  onDataAvailable?:
    | ((event: BlobEvent, streamSid: string | null) => void)
    | null;
  timeslice?: number;
};

export type UseMediaRecorderReturn = {
  isRecording: boolean;
  startRecording: (streamSid?: string | null) => Promise<void>;
  stopRecording: () => void;
  devices: MediaDeviceInfo[];
  selectedDevice: MediaDeviceInfo | null;
  selectDevice: (device: MediaDeviceInfo) => void;
  mediaRecorder: MediaRecorder | null;
  mediaStream: MediaStream | null;
};

const DEFAULT_TIMESLICE = 20;

const useMediaRecorder = (
  options?: UseMediaRecorderOptions,
): UseMediaRecorderReturn => {
  const {
    onDataAvailable,
    timeslice = DEFAULT_TIMESLICE,
    isEnabled = false,
  } = options ?? {};
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<MediaDeviceInfo | null>(
    null,
  );
  const prevSelectedDevice = usePrevious<MediaDeviceInfo | null>(
    selectedDevice,
  );
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const isRecordingRef = useRef(false);
  const [reactiveRecorder, setReactiveRecorder] =
    useState<MediaRecorder | null>(null);
  const [reactiveStream, setReactiveStream] = useState<MediaStream | null>(
    null,
  );
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Track current streamSid to preserve during device switches
  const currentStreamSidRef = useRef<string | null>(null);
  const toast = useToast();

  const updateDeviceList = useCallback(async () => {
    try {
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      const microphoneDevices = deviceList.filter(
        device => device.kind === 'audioinput' && device.deviceId,
      );
      const defaultDevice = microphoneDevices.find(
        device => device.deviceId === 'default',
      );
      setDevices(
        microphoneDevices.filter(device => device.deviceId !== 'default'),
      );
      setSelectedDevice(currentDevice => {
        if (
          currentDevice &&
          microphoneDevices.some(
            device => device.deviceId === currentDevice.deviceId,
          )
        ) {
          return currentDevice;
        }
        return defaultDevice || null;
      });
    } catch (error) {
      if (error instanceof DOMException) {
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'SecurityError'
        ) {
          // microphone permission errors are handled in the get permission function
          return;
        }
      }
      toast.error({
        title: 'Failed to get microphone devices',
      });
    }
  }, [toast]);

  const setupMediaRecorder = useCallback(
    async (streamSid: string | null) => {
      try {
        // stop recording if any, clearing the callback first to discard the
        // final ondataavailable flush that MediaRecorder fires on stop()
        if (recorderRef.current) {
          recorderRef.current.ondataavailable = null;
          recorderRef.current.stop();
        }

        // stop all tracks if any
        streamRef.current?.getTracks().forEach(track => {
          track.stop();
        });

        // Request microphone access
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: selectedDevice ? selectedDevice.deviceId : undefined,
          },
        });
        streamRef.current = stream;
        setReactiveStream(stream);

        // Create media recorder
        const mediaRecorder = new MediaRecorder(stream);
        recorderRef.current = mediaRecorder;
        setReactiveRecorder(mediaRecorder);

        if (onDataAvailable) {
          // Create closure that captures streamSid for THIS recorder instance
          // This ensures each recorder's callbacks are tagged with the correct streamSid
          mediaRecorder.ondataavailable = (event: BlobEvent) => {
            onDataAvailable(event, streamSid);
          };
        }

        mediaRecorder.onstop = () => {
          streamRef.current?.getTracks().forEach(track => track.stop());
        };

        return mediaRecorder;
      } catch (error) {
        if (error instanceof DOMException) {
          if (
            error.name === 'NotAllowedError' ||
            error.name === 'SecurityError'
          ) {
            toast.error({
              title: 'Failed to get microphone access',
            });
            return null;
          }
        }
        console.error('Error occurred', error);
        return null;
      }
    },
    [toast, selectedDevice, onDataAvailable],
  );

  const startRecording = useCallback(
    async (streamSid?: string | null) => {
      if (!isEnabled || isRecordingRef.current) {
        return;
      }

      // Store streamSid for device switching
      currentStreamSidRef.current = streamSid ?? null;

      const mediaRecorder = await setupMediaRecorder(streamSid ?? null);

      if (!mediaRecorder) {
        return;
      }

      // Start recording
      mediaRecorder.start(timeslice);
      isRecordingRef.current = true;
      setIsRecording(true);
    },
    [isEnabled, timeslice, setupMediaRecorder],
  );

  const stopRecording = useCallback(() => {
    if (recorderRef.current) {
      recorderRef.current.ondataavailable = null;
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    setReactiveStream(null);
    currentStreamSidRef.current = null;
    isRecordingRef.current = false;
    setIsRecording(false);
  }, []);

  const selectDevice: UseMediaRecorderReturn['selectDevice'] = useCallback(
    device => {
      setSelectedDevice(device);
    },
    [],
  );

  useEffect(() => {
    // Set default device
    if (
      (selectedDevice && devices.find(device => device === selectedDevice)) ||
      devices.length === 0
    ) {
      return;
    }
    setSelectedDevice(devices[0]);
  }, [selectedDevice, devices]);

  useEffect(() => {
    // Update "ondataavailable" event callback if changed
    if (recorderRef.current) {
      recorderRef.current.ondataavailable = onDataAvailable
        ? (event: BlobEvent) =>
            onDataAvailable(event, currentStreamSidRef.current)
        : null;
    }
  }, [onDataAvailable]);

  useEffect(() => {
    // listen to device changes
    void updateDeviceList();

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    navigator.mediaDevices.addEventListener('devicechange', updateDeviceList);

    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        updateDeviceList,
      );
    };
  }, [updateDeviceList]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing deps are intentional
  useEffect(() => {
    const handlePermissionChange = async () => {
      const status = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      });
      if (status.state !== 'granted') {
        setDevices([]);
        setSelectedDevice(null);
      } else {
        void updateDeviceList();
      }
    };

    void navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then(status => {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        status.addEventListener('change', handlePermissionChange);
      });

    return () => {
      void navigator.permissions
        .query({ name: 'microphone' as PermissionName })
        .then(status => {
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          status.removeEventListener('change', handlePermissionChange);
        });
    };
  }, [updateDeviceList, stopRecording]);

  useEffect(() => {
    if (!isEnabled) {
      stopRecording();
    }
  }, [isEnabled, stopRecording]);

  useEffect(() => {
    // update media stream on mic device switch
    const handleSelectedDeviceChange = async () => {
      if (
        !selectedDevice ||
        !recorderRef.current ||
        !prevSelectedDevice?.deviceId
      ) {
        return;
      }
      if (selectedDevice.deviceId === prevSelectedDevice.deviceId) {
        return;
      }
      // Preserve streamSid during device switch
      const recorder = await setupMediaRecorder(currentStreamSidRef.current);

      if (!recorder) {
        return;
      }

      if (isRecording && isEnabled) {
        recorder.start(timeslice);
      }
    };
    void handleSelectedDeviceChange();
  }, [
    selectedDevice,
    prevSelectedDevice,
    isRecording,
    isEnabled,
    timeslice,
    setupMediaRecorder,
  ]);

  useEffect(() => stopRecording, [stopRecording]);

  return {
    isRecording,
    startRecording,
    stopRecording,
    devices,
    selectedDevice,
    selectDevice,
    mediaRecorder: reactiveRecorder,
    mediaStream: reactiveStream,
  };
};

export default useMediaRecorder;
